import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  XMarkIcon,
  CalendarIcon,
  UsersIcon,
  SparklesIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckIcon,
  PlusIcon,
  InformationCircleIcon,
  GlobeAltIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import { API_BASE_URL } from "../../config/apiConfig";
import { Tour, PricingTier, NationalityType, FinalPrice } from "../../types";
import { usePricing } from "../../contexts/PricingContext";
import { pricingService } from "../../services/pricingService";
import { currencyService } from "../../services/currencyService";
import { countries, getAllCurrencies } from "../../utils/countries";
import MpesaPaymentModal from "../Payment/MpesaPaymentModal";
import { motion, AnimatePresence } from "framer-motion";


interface BookingWizardProps {
  tour: Tour;
  isOpen: boolean;
  onClose: () => void;
}

enum BookingStep {
  SELECTION = "selection",
  TRAVELERS = "travelers",
  EXTRAS = "extras",
  REVIEW = "review",
  PAYMENT = "payment",
}

const BookingWizard: React.FC<BookingWizardProps> = ({
  tour,
  isOpen,
  onClose,
}) => {
  const pricingContext = usePricing();
  const contentRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<BookingStep>(
    BookingStep.SELECTION
  );

  // Phase 1 States: Selection
  // removed local states for startDate, adults, children, tier - using context directly

  const [calculatedPricing, setCalculatedPricing] = useState<FinalPrice | null>(null);
  const [discountedPricing, setDiscountedPricing] = useState<FinalPrice | null>(null);
  const [originalPricing, setOriginalPricing] = useState<FinalPrice | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);

  const isDiscountActive = useMemo(() => {
    const tier = pricingContext.tier || PricingTier.STANDARD;
    return !!tour.discountedTierVariants?.[tier];
  }, [tour.discountedTierVariants, pricingContext.tier]);

  // Payment States
  const [paymentMethod, setPaymentMethod] = useState<string>("card");
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "processing" | "completed" | "failed"
  >("idle");
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // M-Pesa Modal States
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [showPesapalModal, setShowPesapalModal] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);

  // Filter Helpers
  // countries and currencies are now imported from utils
  const availableCurrencies = getAllCurrencies();

  // Phase 2 States: Travelers
  const [travelers, setTravelers] = useState<any[]>([]);

  // Phase 3 States: Extras & Requests
  const [selectedExtras, setSelectedExtras] = useState<any[]>([]);
  const [specialRequests, setSpecialRequests] = useState("");
  const [dismissedGuide, setDismissedGuide] = useState(false);
  const [convertedExtraPrices, setConvertedExtraPrices] = useState<Record<string, number>>({});

  // Mock Extras
  // Dynamic Extras from Admin - Fallback to hardcoded for legacy tours
  const availableExtras = useMemo(() => {
    if (tour.extras && Array.isArray(tour.extras) && tour.extras.length > 0) {
      return tour.extras;
    }
    return [
      {
        id: "balloon",
        name: "Hot Air Balloon Safari",
        price: 450,
        unit: "per person",
        icon: "🎈",
      },
      {
        id: "night_drive",
        name: "Night Game Drive",
        price: 80,
        unit: "per person",
        icon: "🌙",
      },
      {
        id: "village_visit",
        name: "Maasai Village Visit",
        price: 30,
        unit: "per person",
        icon: "🏠",
      },
      {
        id: "photographer",
        name: "Professional Photographer",
        price: 200,
        unit: "per group",
        icon: "📸",
      },
    ];
  }, [tour.extras]);

  // Fetch converted prices for extras when the display currency changes
  useEffect(() => {
    const fetchConvertedPrices = async () => {
      const newPrices: Record<string, number> = {};
      for (const extra of availableExtras) {
        try {
          // Convert from the base price which is assumed to be USD
          newPrices[extra.id] = await currencyService.convert(
            extra.price,
            "USD",
            pricingContext.displayCurrency || "USD"
          );
        } catch (error) {
          console.error(`Failed to convert price for extra ${extra.id}`);
          newPrices[extra.id] = extra.price; // fallback to original
        }
      }
      setConvertedExtraPrices(newPrices);
    };

    fetchConvertedPrices();
  }, [availableExtras, pricingContext.displayCurrency]);

  // Update price when selection changes
  useEffect(() => {
    const updatePrice = async () => {
      // Case-insensitive tier lookup for robustness
      const findTierData = (variants: any, tier: string) => {
        if (!variants) return null;
        return variants[tier] || variants[tier.toUpperCase()] || variants[tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()];
      };

      const tierData = findTierData(tour.tierVariants, pricingContext.tier);

      // Guard: Only calculate if we have dates AND tier data exists
      if (
        !pricingContext.travelDates?.startDate ||
        !tour.tierVariants ||
        Object.keys(tour.tierVariants).length === 0 ||
        !tierData
      ) {
        setCalculatedPricing(null);
        return;
      }

      setIsLoadingPrice(true);
      try {
        const result = await pricingService.calculatePackagePrice(tour as any, {
          ...pricingContext,
          travelDates: {
            startDate: pricingContext.travelDates?.startDate || "",
            endDate: "",
          }, // Service handles duration
        });
        setCalculatedPricing(result);
      } catch (error) {
        console.error("Error calculating price in wizard");
        setCalculatedPricing(null); // Ensure null on error
      } finally {
        setIsLoadingPrice(false);
      }
    };

    updatePrice();
  }, [
    pricingContext.travelDates?.startDate,
    pricingContext.pax.adults,
    pricingContext.pax.children,
    pricingContext.tier,
    tour,
    pricingContext.displayCurrency,
    selectedExtras, // Re-calculate when extras change
  ]);

  // Phase 4: Payment Logic - Automatically default to M-Pesa for local clients
  useEffect(() => {
    // Only auto-switch if we are in the selection step or if nationality just changed
    if (pricingContext.nationalityType === NationalityType.LOCAL) {
      setPaymentMethod("mpesa");
    } else {
      setPaymentMethod("card");
    }
  }, [pricingContext.nationalityType]);

  // Initialize travelers when adults/children change - Non-destructive
  useEffect(() => {
    const adults = Math.max(1, pricingContext.pax.adults ?? 1);
    const children = pricingContext.pax.children ?? 0;
    const totalSelected = adults + children;

    setTravelers(prev => {
      // If the length already matches, just ensure types are correct
      if (prev.length === totalSelected) {
        return prev.map((t, i) => ({
          ...t,
          type: i < adults ? "adult" : "child"
        }));
      }

      const newTravelers = [...prev];

      if (newTravelers.length < totalSelected) {
        // Adding more slots
        const toAdd = totalSelected - newTravelers.length;
        for (let i = 0; i < toAdd; i++) {
          const index = newTravelers.length;
          newTravelers.push({
            id: Date.now() + index + Math.random(), // More unique ID
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            nationality: pricingContext.nationalityType === NationalityType.LOCAL ? "Kenya" : "",
            passportNumber: "",
            type: index < adults ? "adult" : "child",
          });
        }
      } else if (newTravelers.length > totalSelected) {
        // Removing slots (trim from the end)
        newTravelers.splice(totalSelected);
      }

      // Re-assign types based on current distribution
      return newTravelers.map((t, i) => ({
        ...t,
        type: i < adults ? "adult" : "child"
      }));
    });
  }, [
    pricingContext.pax.adults,
    pricingContext.pax.children,
    pricingContext.nationalityType,
    isOpen,
    travelers.length, // Ensure we re-sync if the array is cleared or mismatched
  ]);

  // Persistence: Load state
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem(`bw_state_${tour.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setCurrentStep(parsed.currentStep || BookingStep.SELECTION);
          if (parsed.travelers && parsed.travelers.length > 0) {
            setTravelers(parsed.travelers);
          }
          setSelectedExtras(parsed.selectedExtras || []);
          setSpecialRequests(parsed.specialRequests || "");
          setPaymentMethod(parsed.paymentMethod || "card");

          // Restore pricing context if necessary
          if (parsed.pax)
            pricingContext.setPax(parsed.pax.adults, parsed.pax.children);
          if (parsed.tier) pricingContext.setTier(parsed.tier);
          if (parsed.startDate) pricingContext.setDates(parsed.startDate, "");
          if (parsed.countryCode) pricingContext.setCountry(parsed.countryCode);
          if (parsed.displayCurrency)
            pricingContext.setCurrency(parsed.displayCurrency);
        } catch (e) {
          console.error("Failed to load booking state");
        }
      } else {
        // Default reset if no saved state
        setCurrentStep(BookingStep.SELECTION);

        // Initial traveler generation if no persistence exists
        const adultsCount = Math.max(1, pricingContext.pax.adults || 0);
        const childrenCount = pricingContext.pax.children || 0;
        const total = adultsCount + childrenCount;

        const initialTravelers = Array(total).fill(null).map((_, i) => ({
          id: Date.now() + i,
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          nationality: pricingContext.nationalityType === NationalityType.LOCAL ? "Kenya" : "",
          passportNumber: "",
          type: i < adultsCount ? "adult" : "child",
        }));
        setTravelers(initialTravelers);

        setPaymentStatus("idle");
        setPaymentError(null);
        setShowMpesaModal(false);
        setCreatedBookingId(null);
      }
    }
  }, [isOpen, tour.id]);

  // Persistence: Save state
  useEffect(() => {
    if (isOpen) {
      // Prevent infinite loop by only saving if data has actually changed
      const stateToSave = {
        currentStep,
        travelers,
        selectedExtras,
        specialRequests,
        paymentMethod,
        pax: pricingContext.pax,
        tier: pricingContext.tier,
        startDate: pricingContext.travelDates?.startDate,
        countryCode: pricingContext.countryCode,
        displayCurrency: pricingContext.displayCurrency,
      };
      
      const serialized = JSON.stringify(stateToSave);
      const existing = localStorage.getItem(`bw_state_${tour.id}`);
      
      if (serialized !== existing) {
        localStorage.setItem(`bw_state_${tour.id}`, serialized);
      }
    }
  }, [
    isOpen,
    tour.id,
    currentStep,
    travelers,
    selectedExtras,
    specialRequests,
    paymentMethod,
    pricingContext.pax,
    pricingContext.tier,
    pricingContext.travelDates?.startDate,
    pricingContext.countryCode,
    pricingContext.displayCurrency,
  ]);

  // Scroll to top on step change
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Auto-dismiss concierge guide after 3 seconds when entering payment step
    if (currentStep === BookingStep.PAYMENT && !dismissedGuide) {
      const timer = setTimeout(() => {
        setDismissedGuide(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, dismissedGuide]);

  const toggleExtra = (extra: any) => {
    if (selectedExtras.find((e) => e.id === extra.id)) {
      setSelectedExtras(selectedExtras.filter((e) => e.id !== extra.id));
    } else {
      setSelectedExtras([...selectedExtras, extra]);
    }
  };

  const calculateExtrasTotal = () => {
    const totalPax =
      (pricingContext.pax.adults || 0) + (pricingContext.pax.children || 0);
    return selectedExtras.reduce((total, extra) => {
      // Use converted price if available, otherwise fallback to raw price
      const price = convertedExtraPrices[extra.id] || extra.price;
      if (extra.unit === "per person") {
        return total + price * totalPax;
      }
      return total + price;
    }, 0);
  };

  const getGrandTotal = () => {
    const baseTotal = calculatedPricing?.total || 0;
    return baseTotal + calculateExtrasTotal();
  };

  const handleCheckout = async () => {
    if (!calculatedPricing) return;

    const primaryTraveler = travelers[0];
    const amount = getGrandTotal();

    if (paymentMethod === "mpesa" || paymentMethod === "card") {
      // Create a pending booking first, then open the M-Pesa modal or redirect to Global
      setPaymentStatus("processing");
      try {
        const bookingPayload = {
          tourId: tour.id,
          customerName: `${primaryTraveler.firstName} ${primaryTraveler.lastName}`,
          customerEmail: primaryTraveler.email || "",
          customerPhone:
            paymentMethod === "mpesa"
              ? (() => {
                const p = primaryTraveler.phone || "";
                const c = p.replace(/[^\d]/g, "");
                if (c.startsWith("0")) return "254" + c.slice(1);
                if (c.startsWith("7") || c.startsWith("1")) return "254" + c;
                return c;
              })()
              : primaryTraveler.phone || "",
          customerNationality: pricingContext.countryCode || "KE",
          tourDate: pricingContext.travelDates?.startDate || "",
          specialRequests,
          status: "pending",
          paymentStatus: "pending",

          // Pricing context required by backend BookingService
          tier: pricingContext.tier || "STANDARD",
          nationalityType:
            pricingContext.countryCode === "KE" ? "LOCAL" : "INTERNATIONAL",
          travelDates: {
            startDate: pricingContext.travelDates?.startDate || "",
            endDate: pricingContext.travelDates?.endDate || "",
          },
          pax: {
            adults: pricingContext.pax.adults || 1,
            children: pricingContext.pax.children || 0,
          },
          displayCurrency: calculatedPricing?.currency || "USD",

          // Consent flags
          termsAccepted: true,
          privacyAccepted: true,

          // Optional but helpful: detailed traveler list
          travelers: travelers.map((t) => ({
            name: `${t.firstName} ${t.lastName}`,
            email: t.email,
            phone: t.phone,
            nationality: t.nationality || pricingContext.countryCode,
          })),

          // Explicit total and currency for backend records
          total_amount: amount,
          currency: calculatedPricing?.currency || "USD",
          paymentMethod: paymentMethod === "mpesa" ? "M-Pesa" : paymentMethod === "card" ? "Card/Bank Transfer" : paymentMethod,
          extras: selectedExtras.map(e => ({
            name: e.name,
            price: e.price,
            unit: e.unit,
            description: e.description
          }))
        };

        const res = await fetch(`${API_BASE_URL}/bookings`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bookingPayload),
        });
        const responseData = await res.json();

        if (!res.ok) {
          throw new Error(
            responseData?.error ||
            responseData?.message ||
            "Failed to create booking"
          );
        }

        // Backend returns { success: true, data: { id, ... } }
        const bookingId = responseData.data?.id || responseData.id;
        if (!bookingId) throw new Error("Booking created but no ID returned");

        setCreatedBookingId(bookingId);

        // Persist booking ID for "My Expeditions" visibility
        try {
          const currentBookings = JSON.parse(localStorage.getItem('st_my_bookings') || '[]');
          if (!currentBookings.includes(bookingId)) {
            localStorage.setItem('st_my_bookings', JSON.stringify([...currentBookings, bookingId]));
          }
        } catch (e) {
          console.error("Failed to save booking ID to local history");
        }

        if (paymentMethod === "mpesa") {
          setPaymentStatus("idle");
          setShowMpesaModal(true);
        } else if (paymentMethod === "card") {
          // Pesapal Card Payment Flow
          try {
            const response = await fetch(
              `${API_BASE_URL}/payments/pesapal/initiate`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  bookingId: bookingId,
                  amount: getGrandTotal(),
                  currency: calculatedPricing?.currency || "USD",
                  customerName: `${travelers[0]?.firstName} ${travelers[0]?.lastName}`,
                  customerEmail: travelers[0]?.email,
                  customerPhone: travelers[0]?.phone,
                }),
              }
            );

            const result = await response.json();
            if (result.checkoutUrl) {
              setPaymentStatus("processing");
              window.location.href = result.checkoutUrl;
            } else {
              throw new Error(result.error || "Pesapal initiation failed");
            }
          } catch (err: any) {
            setPaymentStatus("failed");
            setPaymentError(
              err.message || "Pesapal initiation failed. Please try again."
            );
            console.error("Pesapal initiation error");
          }
          return;
        }
      } catch (err: any) {
        setPaymentStatus("failed");
        setPaymentError(
          err.message || "Could not initiate booking. Please try again."
        );
      }
      return;
    }

    // Other methods: bank transfer etc — treat as pending confirmation
    setPaymentStatus("completed");
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 md:p-8">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-xl transition-opacity"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div className="relative w-full h-[92vh] sm:h-auto max-h-[96vh] sm:max-h-[90vh] sm:max-w-5xl bg-white rounded-[2rem] sm:rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col">
          {/* Header */}
          <div className="bg-primary-900 px-4 sm:px-6 py-4 sm:py-6 text-white flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold font-display">
                Book Your Safari
              </h2>
              <p className="text-primary-200 text-sm mt-1">{tour.title}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="bg-gray-50 border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4 overflow-x-auto">
            <div className="flex items-center space-x-3 sm:space-x-6 min-w-max mx-auto justify-center">
              {Object.values(BookingStep).map((step, index) => (
                <React.Fragment key={step}>
                  <button
                    onClick={() => setCurrentStep(step)}
                    className={`flex items-center space-x-2 transition-all hover:opacity-80 ${currentStep === step
                      ? "text-primary-600"
                      : Object.values(BookingStep).indexOf(currentStep) >
                        index
                        ? "text-green-600"
                        : "text-gray-400"
                      }`}
                  >
                    <div
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold border-2 transition-colors ${currentStep === step
                        ? "border-primary-600 bg-primary-50"
                        : Object.values(BookingStep).indexOf(currentStep) >
                          index
                          ? "border-green-600 bg-green-50"
                          : "border-gray-200"
                        }`}
                    >
                      {Object.values(BookingStep).indexOf(currentStep) >
                        index ? (
                        <CheckIcon className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span className="text-sm font-semibold capitalize">
                      {step}
                    </span>
                  </button>
                  {index < Object.values(BookingStep).length - 1 && (
                    <div
                      className={`w-8 sm:w-12 h-px ${Object.values(BookingStep).indexOf(currentStep) > index
                        ? "bg-green-600"
                        : "bg-gray-200"
                        }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto" ref={contentRef}>
            <div className="flex flex-col md:flex-row min-h-full">
              {/* Form Side */}
              <div className="flex-1 md:overflow-y-auto p-4 sm:p-6 md:p-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentStep === BookingStep.SELECTION && (
                      <div className="space-y-10 pb-10">
                        <section className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {/* Nationality Selection */}
                          <div>
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-2">
                              <GlobeAltIcon className="h-4 w-4 mr-2 text-primary-600" />
                              Your Nationality
                            </label>
                            <div className="relative group">
                              <select
                                value={pricingContext.countryCode}
                                onChange={(e) =>
                                  pricingContext.setCountry(e.target.value)
                                }
                                className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 border-gray-100 focus:border-primary-500 appearance-none bg-gray-50/50 font-bold text-sm sm:text-base"
                              >
                                {countries.map((c) => (
                                  <option key={c.code} value={c.code}>
                                    {c.flag} {c.name}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <span className="text-[10px]">▼</span>
                              </div>
                            </div>
                          </div>

                          {/* Currency Selection */}
                          <div>
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-2">
                              <span className="text-lg mr-2 leading-none">
                                💵
                              </span>
                              Preferred Currency
                            </label>
                            <div className="relative group">
                              <select
                                value={pricingContext.displayCurrency}
                                onChange={(e) =>
                                  pricingContext.setCurrency(e.target.value)
                                }
                                className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 border-gray-100 focus:border-primary-500 appearance-none bg-gray-50/50 font-bold text-sm sm:text-base"
                              >
                                {availableCurrencies.map((c) => (
                                  <option key={c}>{c}</option>
                                ))}
                              </select>
                              <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <span className="text-[10px]">▼</span>
                              </div>
                            </div>
                          </div>
                        </section>

                        <section>
                          <label className="flex items-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                            <CalendarIcon className="h-5 w-5 mr-3 text-primary-600" />
                            When are you traveling?
                          </label>
                          <input
                            type="date"
                            value={pricingContext.travelDates?.startDate || ""}
                            onChange={(e) =>
                              pricingContext.setDates(
                                e.target.value,
                                pricingContext.travelDates?.endDate || ""
                              )
                            }
                            className="w-full px-4 sm:px-6 py-4 sm:py-5 rounded-2xl sm:rounded-3xl border-2 border-gray-100 focus:border-primary-500 focus:ring-0 transition-all text-lg sm:text-xl font-bold bg-gray-50/50"
                          />
                          <div className="mt-3 flex items-center text-xs text-gray-500">
                            <InformationCircleIcon className="h-4 w-4 mr-1 text-primary-400" />
                            Tour Duration:{" "}
                            <span className="font-bold ml-1 text-gray-700">
                              {tour.duration}
                            </span>
                          </div>
                        </section>

                        <section>
                          <label className="flex items-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                            <UsersIcon className="h-5 w-5 mr-3 text-primary-600" />
                            Who's coming along?
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="p-6 rounded-3xl border-2 border-gray-100 bg-gray-50/50">
                              <span className="block text-xs font-bold text-gray-400 uppercase mb-4">
                                Adults
                              </span>
                              <div className="flex items-center justify-between">
                                <button
                                  onClick={() =>
                                    pricingContext.setPax(
                                      Math.max(
                                        1,
                                        (pricingContext.pax.adults || 1) - 1
                                      ),
                                      pricingContext.pax.children || 0
                                    )
                                  }
                                  className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center hover:bg-primary-50 hover:text-primary-600 transition-colors border border-gray-100 font-bold text-xl"
                                >
                                  -
                                </button>
                                <span className="text-3xl font-black text-primary-900">
                                  {pricingContext.pax.adults || 1}
                                </span>
                                <button
                                  onClick={() =>
                                    pricingContext.setPax(
                                      (pricingContext.pax.adults || 1) + 1,
                                      pricingContext.pax.children || 0
                                    )
                                  }
                                  className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center hover:bg-primary-50 hover:text-primary-600 transition-colors border border-gray-100 font-bold text-xl"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            <div className="p-6 rounded-3xl border-2 border-gray-100 bg-gray-50/50">
                              <span className="block text-xs font-bold text-gray-400 uppercase mb-4">
                                Children
                              </span>
                              <div className="flex items-center justify-between">
                                <button
                                  onClick={() =>
                                    pricingContext.setPax(
                                      pricingContext.pax.adults || 1,
                                      Math.max(
                                        0,
                                        (pricingContext.pax.children || 0) - 1
                                      )
                                    )
                                  }
                                  className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center hover:bg-primary-50 hover:text-primary-600 transition-colors border border-gray-100 font-bold text-xl"
                                >
                                  -
                                </button>
                                <span className="text-3xl font-black text-primary-900">
                                  {pricingContext.pax.children || 0}
                                </span>
                                <button
                                  onClick={() =>
                                    pricingContext.setPax(
                                      pricingContext.pax.adults || 1,
                                      (pricingContext.pax.children || 0) + 1
                                    )
                                  }
                                  className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center hover:bg-primary-50 hover:text-primary-600 transition-colors border border-gray-100 font-bold text-xl"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </section>

                        <section>
                          <label className="flex items-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                            <SparklesIcon className="h-5 w-5 mr-3 text-primary-600" />
                            Choose Your Experience
                          </label>
                          <div className="grid gap-4">
                            {[
                              PricingTier.LUXURY,
                              PricingTier.STANDARD,
                              PricingTier.BUDGET,
                            ].map((tier) => (
                              <button
                                key={tier}
                                onClick={() => pricingContext.setTier(tier)}
                                className={`group relative flex items-center justify-between p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-2 transition-all duration-500 ${pricingContext.tier === tier
                                  ? "border-primary-600 bg-primary-50/50 shadow-xl shadow-primary-100/20 sm:translate-x-2"
                                  : "border-gray-100 hover:border-gray-200 bg-white"
                                  }`}
                              >
                                <div className="flex items-center">
                                  <div
                                    className={`w-6 h-6 rounded-full border-2 mr-6 flex items-center justify-center transition-all ${pricingContext.tier === tier
                                      ? "border-primary-600 bg-primary-600"
                                      : "border-gray-200"
                                      }`}
                                  >
                                    {pricingContext.tier === tier && (
                                      <CheckIcon className="h-4 w-4 text-white" />
                                    )}
                                  </div>
                                  <div className="text-left">
                                    <span
                                      className={`block font-black text-lg capitalize ${pricingContext.tier === tier
                                        ? "text-primary-900"
                                        : "text-gray-700"
                                        }`}
                                    >
                                      {tier} Experience
                                    </span>
                                    <span className="text-sm text-gray-500 font-medium">
                                      Authoritative standard of hospitality
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="text-xs font-bold text-primary-600 bg-primary-100 px-3 py-1 rounded-full uppercase tracking-tighter">
                                    Recommended
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </section>
                      </div>
                    )}

                    {currentStep === BookingStep.TRAVELERS && (
                      <div className="space-y-8 pb-10">
                        <div className="flex items-center justify-between mb-8">
                          <div>
                            <h3 className="text-2xl font-black text-gray-900">
                              Traveler Details
                            </h3>
                            <p className="text-gray-500 text-sm">
                              We need some information to finalize your
                              manifest.
                            </p>
                          </div>
                          <span className="text-sm font-bold text-primary-600 bg-primary-50 px-4 py-2 rounded-2xl">
                            {travelers.length} Manifest Slots
                          </span>
                        </div>

                        {travelers.map((traveler, index) => (
                          <div
                            key={index}
                            className="p-8 rounded-[40px] border-2 border-gray-100 space-y-6 bg-white hover:border-primary-100 transition-colors group"
                          >
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="w-8 h-8 rounded-2xl bg-primary-900 text-white flex items-center justify-center text-xs font-black shadow-lg shadow-primary-900/20">
                                {index + 1}
                              </div>
                              <span className="font-black text-primary-900 capitalize tracking-tight">
                                {traveler.type}{" "}
                                {index === 0
                                  ? "— Primary Contact"
                                  : `— Traveler ${index + 1}`}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-400 uppercase ml-2">
                                  First Name
                                </label>
                                <input
                                  type="text"
                                  placeholder="As on passport"
                                  value={traveler.firstName}
                                  onChange={(e) => {
                                    const newTravelers = [...travelers];
                                    newTravelers[index].firstName =
                                      e.target.value;
                                    setTravelers(newTravelers);
                                  }}
                                  className="w-full px-6 py-4 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary-500 transition-all font-bold"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-400 uppercase ml-2">
                                  Last Name
                                </label>
                                <input
                                  type="text"
                                  placeholder="As on passport"
                                  value={traveler.lastName}
                                  onChange={(e) => {
                                    const newTravelers = [...travelers];
                                    newTravelers[index].lastName =
                                      e.target.value;
                                    setTravelers(newTravelers);
                                  }}
                                  className="w-full px-6 py-4 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary-500 transition-all font-bold"
                                />
                              </div>
                            </div>

                            {index === 0 && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 border-t border-gray-50">
                                <div className="space-y-2">
                                  <label className="block text-[10px] font-black text-gray-400 uppercase ml-2">
                                    Email Address
                                  </label>
                                  <input
                                    type="email"
                                    placeholder="For confirmation"
                                    value={traveler.email}
                                    onChange={(e) => {
                                      const newTravelers = [...travelers];
                                      newTravelers[index].email =
                                        e.target.value;
                                      setTravelers(newTravelers);
                                    }}
                                    className="w-full px-6 py-4 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary-500 transition-all font-bold"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="block text-[10px] font-black text-gray-400 uppercase ml-2">
                                    WhatsApp / Phone
                                  </label>
                                  <input
                                    type="tel"
                                    placeholder={
                                      index === 0 && paymentMethod === "mpesa"
                                        ? "07xx xxx xxx"
                                        : "+1 234..."
                                    }
                                    value={traveler.phone}
                                    onChange={(e) => {
                                      const newTravelers = [...travelers];
                                      newTravelers[index].phone =
                                        e.target.value;
                                      setTravelers(newTravelers);
                                    }}
                                    className={`w-full px-6 py-4 rounded-2xl border bg-gray-50 focus:bg-white focus:border-primary-500 transition-all font-bold ${index === 0 &&
                                      paymentMethod === "mpesa" &&
                                      traveler.phone &&
                                      !traveler.phone.match(
                                        /^(?:254|\\+254|0)?(7|1)\\d{8}$/
                                      )
                                      ? "border-red-300"
                                      : "border-gray-200"
                                      }`}
                                  />
                                  {index === 0 && paymentMethod === "mpesa" && (
                                    <p className="text-[10px] text-gray-500 ml-2 mt-1">
                                      Required for M-Pesa prompt.
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === BookingStep.EXTRAS && (
                      <div className="space-y-8 pb-10">
                        <div className="mb-8">
                          <h3 className="text-3xl font-black text-gray-900">
                            Safari Extras
                          </h3>
                          <p className="text-gray-500">
                            Enhance your adventure with these hand-picked
                            experiences.
                          </p>
                        </div>

                        <div className="grid gap-6">
                          {availableExtras.map((extra) => (
                            <button
                              key={extra.id}
                              onClick={() => toggleExtra(extra)}
                              className={`flex items-center justify-between p-8 rounded-[40px] border-2 transition-all duration-500 ${selectedExtras.find((e) => e.id === extra.id)
                                ? "border-primary-600 bg-primary-50/50 shadow-xl shadow-primary-100/20 translate-y-[-4px]"
                                : "border-gray-100 hover:border-gray-200 bg-white"
                                }`}
                            >
                              <div className="flex items-center">
                                <div className="w-16 h-16 rounded-3xl bg-gray-100 flex items-center justify-center text-3xl mr-6 shadow-inner">
                                  {extra.icon}
                                </div>
                                <div className="text-left">
                                  <span className="block font-black text-lg text-gray-900">
                                    {extra.name}
                                  </span>
                                  <span className="text-sm font-bold text-primary-600">
                                    {currencyService.formatCurrency(
                                      convertedExtraPrices[extra.id] !== undefined ? convertedExtraPrices[extra.id] : extra.price,
                                      pricingContext.displayCurrency || "USD"
                                    )}{" "}
                                    <span className="text-gray-400 font-medium lowercase italic">
                                      / {extra.unit}
                                    </span>
                                  </span>
                                </div>
                              </div>
                              <div
                                className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all ${selectedExtras.find((e) => e.id === extra.id)
                                  ? "bg-primary-600 text-white"
                                  : "bg-gray-100 text-gray-400"
                                  }`}
                              >
                                {selectedExtras.find(
                                  (e) => e.id === extra.id
                                ) ? (
                                  <CheckIcon className="h-6 w-6" />
                                ) : (
                                  <PlusIcon className="h-6 w-6" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>

                        <div className="pt-10">
                          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
                            Any special requests / dietary needs?
                          </label>
                          <textarea
                            value={specialRequests}
                            onChange={(e) => setSpecialRequests(e.target.value)}
                            placeholder="Tell us about allergies, birthdays, or specific interests..."
                            className="w-full px-8 py-6 rounded-[40px] border-2 border-gray-100 bg-gray-50 focus:bg-white focus:border-primary-500 transition-all font-medium h-40 resize-none"
                          />
                        </div>
                      </div>
                    )}

                    {currentStep === BookingStep.REVIEW && (
                      <div className="space-y-10 pb-10">
                        <div className="mb-4">
                          <h3 className="text-3xl font-black text-gray-900">
                            Review Your Safari
                          </h3>
                          <p className="text-gray-500">
                            Double check everything before we proceed to
                            payment.
                          </p>
                        </div>

                        {/* Itinerary Summary Card */}
                        <div className="bg-primary-900 rounded-[50px] p-10 text-white shadow-2xl relative overflow-hidden">
                          <div className="absolute top-0 right-0 p-8 opacity-10">
                            <CalendarIcon className="h-40 w-40" />
                          </div>
                          <div className="relative z-10 grid grid-cols-2 gap-10">
                            <div className="space-y-2">
                              <span className="text-xs font-black text-primary-300 uppercase tracking-widest">
                                Adventure
                              </span>
                              <h4 className="text-2xl font-black leading-tight">
                                {tour.title}
                              </h4>
                            </div>
                            <div className="space-y-4">
                              <div className="flex items-center space-x-4">
                                <CalendarIcon className="h-6 w-6 text-primary-400" />
                                <div>
                                  <span className="block text-[10px] font-black text-primary-400 uppercase">
                                    Departure Date
                                  </span>
                                  <span className="font-bold text-lg">
                                    {pricingContext.travelDates?.startDate
                                      ? new Date(
                                        pricingContext.travelDates.startDate
                                      ).toLocaleDateString("en-US", {
                                        dateStyle: "full",
                                      })
                                      : "Select Date"}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-4">
                                <UsersIcon className="h-6 w-6 text-primary-400" />
                                <div>
                                  <span className="block text-[10px] font-black text-primary-400 uppercase">
                                    Group Size
                                  </span>
                                  <span className="font-bold text-lg">
                                    {(pricingContext.pax.adults || 0) +
                                      (pricingContext.pax.children || 0)}{" "}
                                    Passengers
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Manifest Review */}
                        <div className="space-y-6">
                          <h5 className="text-xs font-black text-gray-400 uppercase tracking-widest ml-2">
                            Passenger Manifest
                          </h5>
                          <div className="grid gap-4">
                            {travelers.map((t, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between p-6 rounded-3xl border-2 border-gray-50 bg-gray-50/30"
                              >
                                <div className="flex items-center space-x-4">
                                  <div className="w-10 h-10 rounded-2xl bg-white border border-gray-100 flex items-center justify-center font-black text-primary-900">
                                    {i + 1}
                                  </div>
                                  <div>
                                    <span className="block font-bold text-gray-900">
                                      {t.firstName} {t.lastName}
                                    </span>
                                    <span className="text-xs text-gray-500 uppercase font-black tracking-tighter">
                                      {t.type}
                                    </span>
                                  </div>
                                </div>
                                {i === 0 && (
                                  <span className="text-[10px] font-black bg-primary-900 text-white px-3 py-1 rounded-full uppercase">
                                    Lead
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Extras Review */}
                        {selectedExtras.length > 0 && (
                          <div className="space-y-6">
                            <h5 className="text-xs font-black text-gray-400 uppercase tracking-widest ml-2">
                              Selected Enhancements
                            </h5>
                            <div className="grid gap-4">
                              {selectedExtras.map((e, i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between p-6 rounded-3xl border-2 border-primary-50 bg-primary-50/20"
                                >
                                  <div className="flex items-center space-x-4">
                                    <span className="text-2xl">{e.icon}</span>
                                    <span className="font-bold text-primary-900">
                                      {e.name}
                                    </span>
                                  </div>
                                  <span className="font-black text-primary-600">
                                    {currencyService.formatCurrency(
                                      convertedExtraPrices[e.id] !== undefined ? convertedExtraPrices[e.id] : e.price,
                                      pricingContext.displayCurrency || "USD"
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Advanced Price Breakdown */}
                        {calculatedPricing && (
                          <div className="space-y-6 pt-6 border-t border-gray-100">
                            <h5 className="text-xs font-black text-gray-400 uppercase tracking-widest ml-2">
                              Authoritative Breakdown
                            </h5>
                            <div className="bg-gray-50 rounded-[40px] p-8 space-y-4">
                              {calculatedPricing.breakdown.map((item, i) => (
                                <div
                                  key={i}
                                  className="flex justify-between items-start gap-4"
                                >
                                  <div className="flex-1">
                                    <span className="block font-bold text-gray-900">
                                      {item.item}
                                    </span>
                                    <span className="text-[10px] text-gray-500 leading-relaxed block">
                                      {item.description}
                                    </span>
                                  </div>
                                  <span
                                    className={`font-black whitespace-nowrap ${item.type === "tax"
                                      ? "text-green-600"
                                      : "text-primary-900"
                                      }`}
                                  >
                                    {currencyService.formatCurrency(
                                      item.total,
                                      calculatedPricing.currency
                                    )}
                                  </span>
                                </div>
                              ))}
                              <div className="pt-4 border-t border-gray-200 mt-4 flex justify-between items-center">
                                <span className="text-sm font-black text-gray-900 uppercase">
                                  Safari Subtotal
                                </span>
                                <span className="text-xl font-black text-primary-900">
                                  {currencyService.formatCurrency(
                                    calculatedPricing.total,
                                    calculatedPricing.currency
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-start space-x-3 px-4 py-4 bg-primary-50 rounded-3xl border border-primary-100">
                              <InformationCircleIcon className="h-5 w-5 text-primary-600 shrink-0" />
                              <p className="text-[10px] font-medium text-primary-800 leading-relaxed">
                                Pricing reflects current **
                                {pricingContext.nationalityType}** rates and **
                                {calculatedPricing.seasonMultiplier}x** seasonal
                                multiplier for{" "}
                                {pricingContext.travelDates?.startDate
                                  ? new Date(
                                    pricingContext.travelDates.startDate
                                  ).toLocaleDateString("en-US", {
                                    month: "long",
                                  })
                                  : "Selected Date"}
                                .
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {currentStep === BookingStep.PAYMENT && (
                      <div className="space-y-8 pb-10 relative">
                        {/* Safari Concierge Guide */}
                        <AnimatePresence>
                          {!dismissedGuide && (
                            <motion.div
                              drag="x"
                              dragConstraints={{ left: 0, right: 0 }}
                              onDragEnd={(_, info) => {
                                if (Math.abs(info.offset.x) > 100) setDismissedGuide(true);
                              }}
                              initial={{ opacity: 0, scale: 0.9, y: 20 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.9, y: 10 }}
                              className="mb-8 p-5 rounded-[2rem] bg-white/10 dark:bg-black/20 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] relative overflow-hidden group cursor-grab active:cursor-grabbing"
                            >

                              <div className="relative z-10 flex items-center space-x-4">
                                <div className="flex-shrink-0 relative">
                                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-inner transition-colors duration-500 ${pricingContext.nationalityType === NationalityType.LOCAL ? 'bg-green-500/20' : 'bg-primary-500/20'
                                    }`}>
                                    {pricingContext.nationalityType === NationalityType.LOCAL ? '🇰🇪' : '🌍'}
                                  </div>
                                </div>

                                <div className="flex-1 pt-1">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${pricingContext.nationalityType === NationalityType.LOCAL ? 'bg-green-500' : 'bg-primary-500'
                                        }`} />
                                      <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">Concierge Advice</h4>
                                    </div>
                                    <button
                                      onClick={() => setDismissedGuide(true)}
                                      className="text-gray-500 hover:text-white transition-colors"
                                    >
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  </div>

                                  <p className="text-sm font-bold text-gray-800 dark:text-white leading-tight">
                                    {pricingContext.nationalityType === NationalityType.LOCAL ? (
                                      <>
                                        Recommended: <span className="text-green-600 dark:text-green-400 font-black">M-Pesa</span> for instant Kenyan confirmation.
                                      </>
                                    ) : (
                                      <>
                                        Recommended: <span className="text-primary-600 dark:text-primary-400 font-black">Credit Card</span> for global secure pay.
                                      </>
                                    )}
                                  </p>


                                </div>
                              </div>

                              {/* Swiper Visual Indicator (Mobile Only) */}
                              <div className="mt-3 flex justify-center md:hidden pb-1">
                                <div className="w-8 h-1 rounded-full bg-gray-400/20" />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="mb-4">
                          <h3 className="text-3xl font-black text-gray-900">
                            Secure Checkout
                          </h3>
                          <p className="text-gray-500 font-medium">
                            Safe & encrypted payment processed by Safari-Shield™
                          </p>
                        </div>

                        <div className="grid gap-6">
                          <div className="p-8 rounded-[40px] border-2 border-primary-600 bg-primary-50/30 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform duration-500">
                              <GlobeAltIcon className="h-24 w-24" />
                            </div>
                            <div className="relative z-10">
                              <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-600 text-white text-[10px] font-black uppercase tracking-widest mb-4">
                                Official Payment Gateway
                              </span>
                              <h4 className="text-xl font-black text-primary-900 mb-2">
                                Direct Bank Transfer or Card
                              </h4>
                              <p className="text-sm text-primary-800/70 font-medium leading-relaxed max-w-md">
                                After confirming, our concierge will contact you
                                with specific bank details for your selected
                                currency (**{calculatedPricing?.currency}**) or
                                a secure Stripe link.
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                            <button
                              onClick={() => setShowPesapalModal(true)}
                              className={`h-32 rounded-3xl border-2 flex items-center justify-center transition-all ${paymentMethod === "card"
                                ? "border-blue-500 bg-blue-50 shadow-[0_10px_30px_-10px_rgba(59,130,246,0.2)]"
                                : "border-gray-100 bg-white hover:border-gray-200"
                                }`}
                            >
                              <img
                                src="/assets/payment/pesapal.png"
                                className={`h-12 w-auto object-contain transition-all duration-500 ${paymentMethod === "card" ? "scale-110" : "scale-100"}`}
                                alt="PesaPal"
                              />
                            </button>

                            <button
                              onClick={() => setPaymentMethod("mpesa")}
                              className={`h-32 rounded-3xl border-2 flex items-center justify-center transition-all ${paymentMethod === "mpesa"
                                ? "border-green-500 bg-green-50/50 shadow-[0_10px_30px_-10px_rgba(34,197,94,0.1)]"
                                : "border-gray-100 bg-white hover:border-gray-200"
                                }`}
                            >
                              <img
                                src="/assets/payment/mpesa.svg"
                                className={`h-10 w-auto object-contain transition-all duration-500 ${paymentMethod === "mpesa" ? "scale-110" : "scale-100"}`}
                                alt="M-Pesa"
                              />
                            </button>
                          </div>

                          <div className="pt-6 border-t border-gray-100">
                            <div className="flex items-center space-x-3 text-gray-500">
                              <CheckIcon className="h-5 w-5 text-green-500" />
                              <span className="text-sm font-bold">
                                256-bit SSL Encrypted Connection
                              </span>
                            </div>
                            <div className="flex items-center space-x-3 text-gray-500 mt-2">
                              <CheckIcon className="h-5 w-5 text-green-500" />
                              <span className="text-sm font-bold">
                                IATA Accredited Agency
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
              {/* Sidebar Summary */}
              <div className="w-full md:w-96 bg-gray-50 p-6 sm:p-8 md:p-10 border-t md:border-t-0 md:border-l border-gray-100 md:overflow-y-auto flex-shrink-0">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-8 border-b border-gray-200 pb-4">
                  Booking Summary
                </h3>

                <div className="space-y-6 mb-10">
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-gray-400 uppercase tracking-tighter">
                        Package Base
                      </span>
                      <span className="text-sm font-bold text-gray-700 capitalize">
                        {pricingContext.tier} for {pricingContext.pax.adults}{" "}
                        {pricingContext.pax.adults === 1 ? "Adult" : "Adults"}
                        {pricingContext.pax.children > 0 &&
                          ` & ${pricingContext.pax.children} ${pricingContext.pax.children === 1
                            ? "Child"
                            : "Children"
                          }`}
                      </span>
                    </div>
                    <span className="font-black text-primary-900">
                      {calculatedPricing
                        ? currencyService.formatCurrency(
                          calculatedPricing.total,
                          calculatedPricing.currency
                        )
                        : "--"}
                    </span>
                  </div>

                  {selectedExtras.map((extra) => (
                    <div
                      key={extra.id}
                      className="flex justify-between items-center animate-in slide-in-from-right duration-300"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-black text-gray-400 uppercase tracking-tighter">
                          Extra
                        </span>
                        <span className="text-sm font-bold text-gray-700">
                          {extra.name}
                        </span>
                      </div>
                      <span className="font-black text-primary-900">
                        {currencyService.formatCurrency(
                          extra.unit === "per person"
                            ? (convertedExtraPrices[extra.id] || extra.price) *
                            ((pricingContext.pax.adults || 0) +
                              (pricingContext.pax.children || 0))
                            : (convertedExtraPrices[extra.id] || extra.price),
                          pricingContext.displayCurrency || "USD"
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="p-8 bg-white rounded-[40px] shadow-2xl shadow-primary-900/5 mb-8 border border-primary-50 relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-5">
                    <SparklesIcon className="h-20 w-20 text-primary-600" />
                  </div>
                  <span className="block text-xs font-black text-gray-400 uppercase mb-3 tracking-widest">
                    Grand Total
                  </span>
                  <div className="text-4xl font-black text-primary-900 tracking-tighter">
                    {isLoadingPrice ? (
                      <div className="h-10 w-40 bg-gray-100 animate-pulse rounded-2xl" />
                    ) : !calculatedPricing ? (
                      <span className="text-2xl text-accent-600 uppercase">
                        Pricing Pending
                      </span>
                    ) : (
                      <div className="flex flex-col items-end">
                        {isDiscountActive && (
                          <span className="text-sm text-gray-400 line-through font-bold mb-1">
                            {currencyService.formatCurrency(
                              (calculatedPricing as any).originalTotal || getGrandTotal(),
                              calculatedPricing.currency
                            )}
                          </span>
                        )}
                        <span>
                          {currencyService.formatCurrency(
                            getGrandTotal(),
                            calculatedPricing.currency
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center text-[10px] font-black text-green-600 uppercase tracking-widest">
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Price Guaranteed
                  </div>
                </div>

                <div className="space-y-4">
                  {currentStep === BookingStep.SELECTION && (
                    <button
                      disabled={
                        !pricingContext.travelDates?.startDate ||
                        isLoadingPrice ||
                        !calculatedPricing
                      }
                      onClick={() => setCurrentStep(BookingStep.TRAVELERS)}
                      className="w-full group bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white font-black py-6 px-10 rounded-[30px] shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] transition-all flex items-center justify-center space-x-3 active:scale-95"
                    >
                      <span className="text-lg">
                        {!calculatedPricing
                          ? "Pricing Pending"
                          : "Finalize Details"}
                      </span>
                      <ChevronRightIcon className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
                    </button>
                  )}

                  {currentStep === BookingStep.TRAVELERS && (
                    <>
                      <button
                        disabled={
                          travelers.some((t) => !t.firstName || !t.lastName) ||
                          !travelers[0]?.email ||
                          !travelers[0]?.phone ||
                          (paymentMethod === "mpesa" &&
                            !travelers[0]?.phone.match(
                              /^(?:254|\+254|0)?(7|1)\d{8}$/
                            ))
                        }
                        onClick={() => setCurrentStep(BookingStep.EXTRAS)}
                        className="w-full group bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white font-black py-6 px-10 rounded-[30px] shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] transition-all flex items-center justify-center space-x-3 active:scale-95"
                      >
                        <span className="text-lg">Choose Extras</span>
                        <ChevronRightIcon className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
                      </button>
                      <button
                        onClick={() => setCurrentStep(BookingStep.SELECTION)}
                        className="w-full text-gray-400 font-bold py-4 hover:text-gray-600 transition-colors uppercase text-xs tracking-widest"
                      >
                        Back to Logistics
                      </button>
                    </>
                  )}

                  {currentStep === BookingStep.EXTRAS && (
                    <>
                      <button
                        onClick={() => setCurrentStep(BookingStep.REVIEW)}
                        className="w-full group bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white font-black py-6 px-10 rounded-[30px] shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] transition-all flex items-center justify-center space-x-3 active:scale-95"
                      >
                        <span className="text-lg">Review Manifest</span>
                        <ChevronRightIcon className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
                      </button>
                      <button
                        onClick={() => setCurrentStep(BookingStep.TRAVELERS)}
                        className="w-full text-gray-400 font-bold py-4 hover:text-gray-600 transition-colors uppercase text-xs tracking-widest"
                      >
                        Back to Travelers
                      </button>
                    </>
                  )}

                  {currentStep === BookingStep.REVIEW && (
                    <>
                      <button
                        onClick={() => {
                          const defaultMethod = pricingContext.nationalityType === NationalityType.LOCAL ? "mpesa" : "card";
                          setPaymentMethod(defaultMethod);
                          setCurrentStep(BookingStep.PAYMENT);
                        }}
                        className="w-full group bg-primary-900 hover:bg-black text-white font-bold py-6 px-10 rounded-[30px] shadow-2xl transition-all flex items-center justify-center space-x-3 active:scale-95"
                      >
                        <span className="text-lg">Proceed to Checkout</span>
                        <ChevronRightIcon className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() => setCurrentStep(BookingStep.EXTRAS)}
                        className="w-full text-gray-400 font-bold py-4 hover:text-gray-600 transition-colors uppercase text-xs tracking-widest"
                      >
                        Modify Extras
                      </button>
                    </>
                  )}

                  {currentStep === BookingStep.PAYMENT && (
                    <>
                      {paymentStatus === "idle" && (
                        <button
                          onClick={handleCheckout}
                          className={`w-full group ${paymentMethod === "mpesa"
                            ? "bg-green-600 hover:bg-green-700 shadow-[0_20px_40px_-15px_rgba(22,163,74,0.4)]"
                            : "bg-blue-600 hover:bg-blue-700 shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)]"
                            } text-white font-bold py-6 px-10 rounded-[30px] transition-all flex items-center justify-center space-x-3 active:scale-95`}
                        >
                          <span className="text-lg">
                            {paymentMethod === "mpesa"
                              ? "Pay via M-Pesa"
                              : "Pay with Card"}
                          </span>
                          <ChevronRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                        </button>
                      )}

                      {paymentStatus === "processing" && (
                        <div className="p-6 bg-yellow-50 rounded-3xl border border-yellow-100 text-center space-y-3">
                          <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
                          <p className="text-sm font-bold text-yellow-800">
                            Processing secure payment...
                          </p>
                          {paymentMethod === "mpesa" ? (
                            <p className="text-xs text-yellow-600">
                              Please check your phone for the M-Pesa prompt.
                            </p>
                          ) : (
                            <p className="text-xs text-yellow-600">
                              Redirecting to secure card checkout...
                            </p>
                          )}
                        </div>
                      )}

                      {paymentStatus === "completed" && (
                        <div className="p-6 bg-green-50 rounded-3xl border border-green-100 text-center space-y-2">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600">
                            <CheckIcon className="w-6 h-6" />
                          </div>
                          <h4 className="font-black text-green-800">
                            Booking Confirmed!
                          </h4>
                          {/* Removed receipt email text as per user request */}
                        </div>
                      )}

                      {paymentStatus === "failed" && (
                        <div className="p-6 bg-red-50 rounded-3xl border border-red-100 space-y-4">
                          <div className="text-center">
                            <h4 className="font-black text-red-800">
                              Payment Unsuccessful
                            </h4>
                            <p className="text-xs text-red-600 mt-2 font-medium bg-white/50 p-2 rounded-lg">
                              {paymentError}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setPaymentStatus("idle");
                              setPaymentError(null);
                            }}
                            className="w-full py-3 bg-red-100 hover:bg-red-200 text-red-800 rounded-xl font-bold text-sm transition-colors"
                          >
                            Modify Details & Try Again
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMpesaModal && createdBookingId && (
        <MpesaPaymentModal
          isOpen={showMpesaModal}
          bookingId={createdBookingId!}
          amount={getGrandTotal()}
          currency={calculatedPricing?.currency || "KES"}
          tourName={tour.title}
          onSuccess={(_receipt: string) => {
            setShowMpesaModal(false);
            setPaymentStatus("completed");
          }}
          onClose={() => {
            setShowMpesaModal(false);
            setPaymentStatus("idle");
          }}
        />
      )
      }

      {/* PesaPal Maintenance Modal */}
      <AnimatePresence>
        {showPesapalModal && (
          <div className="fixed inset-0 z-[12000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl relative"
            >
              <button
                onClick={() => setShowPesapalModal(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              
              <div className="mb-6">
                <img src="/assets/payment/pesapal.png" alt="PesaPal" className="h-8 w-auto mb-6 opacity-80" />
                <h3 className="text-xl font-black text-slate-900 mb-4">Integration Under Development</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Pesapal integration is still under development, and you will be emailed once it is working.
                </p>
                <div className="bg-primary-50 p-4 rounded-2xl border border-primary-100">
                  <h4 className="text-xs font-black text-primary-900 uppercase tracking-widest mb-2">Alternative Methods</h4>
                  <p className="text-sm text-primary-800 leading-relaxed">
                    In the meantime, please pay via <strong>M-Pesa</strong> using our till number, or contact us through <strong>+254 708 560665</strong> or <strong>sighttrailsafaris@gmail.com</strong> for alternative international payment methods.
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowPesapalModal(false);
                  setPaymentMethod("mpesa");
                }}
                className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-black transition-colors"
              >
                Pay via M-Pesa Instead
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BookingWizard;
